import React, { useState, useEffect, useRef } from "react";
import Tooltip from "./Tooltip"; // Add this line
import { getAuth } from "firebase/auth";
import useAuthUser from "../hooks/useAuthUser";
import { getUserData } from "../services/userService";
import { useNotifications } from "../hooks/notificationsContext";

export default function WorkPanel() {
  const authUser = useAuthUser();
  const [selected, setSelected] = useState("TechnicalAnalysis");
  const [workingUntil, setWorkingUntil] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [earnings, setEarnings] = useState({ money: 0, xp: 0, gold: 0 });
  const [userData, setUserData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [, setFrozen] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const { addNotification } = useNotifications();
  
  const manualStartLock = useRef(0);

  const workOptions = [
    { value: "TechnicalAnalysis", label: "Technical Analysis" },
    { value: "SignalHunting", label: "Signal Hunting" },
    { value: "AcceleratedTrading", label: "Accelerated Trading" },
    { value: "MarketProspecting", label: "Market Prospecting" },
  ];

  const fmtRemain = (ms) => {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    let out = "";
    if (h > 0) out += `${h}h `;
    if (m > 0 || h > 0) out += `${m}m `;
    out += `${s}s`;
    return out.trim();
  };

useEffect(() => {
  const restore = async () => {
    try {
      const uid = getAuth().currentUser?.uid;
      if (!uid) return;
      const data = await getUserData(uid);
      setUserData(data);

      if (Date.now() - manualStartLock.current < 10000) return;

      const workSel = data.userWorkSelected ?? "none";
      const untilMs = Number(data.userWorkingUntil || 0);

      let actualStartMs = 0;
      if (data.userWorkStartedAt) {
        actualStartMs = data.userWorkStartedAt.toMillis 
          ? data.userWorkStartedAt.toMillis() 
          : Number(data.userWorkStartedAt);
      }

      if (workSel !== "none" && untilMs > 0) {
        setSelected(workSel);
        setWorkingUntil(untilMs);
        
        if (actualStartMs > 0) {
          setStartedAt(actualStartMs);
        } else {
          const userRank = data.userRank ?? 1;
          const maxWorkMs = (60 + (userRank - 1) * 30) * 60 * 1000;
          setStartedAt(untilMs - maxWorkMs);
        }
      }
    } catch (err) {
      console.error("Restore failed:", err);
    }
  };
  restore();
}, [authUser]);

useEffect(() => {
  if (!workingUntil || !startedAt || !userData || isStarting) return;

  const interval = setInterval(() => {
    const now = Date.now();
    const totalElapsedMs = now - startedAt;
    
    const blockMs = 10 * 60 * 1000;
    const cliffMs = 30 * 60 * 1000;

    let blocksToDisplay = 0;

    if (totalElapsedMs >= cliffMs) {
      blocksToDisplay = Math.floor(totalElapsedMs / blockMs);
    }

    if (blocksToDisplay > 0) {
      const userRank = userData.userRank ?? 1;
      const mult = 1.5 + (userRank / 12);
      
      let gold = blocksToDisplay * (0.5 / 6) * mult; 

      setEarnings(prev => ({ ...prev, gold }));
    } else {
      setEarnings({ money: 0, xp: 0, gold: 0 });
    }

    setTimeLeft(fmtRemain(Math.max(0, workingUntil - now)));
  }, 1000);

  return () => clearInterval(interval);
}, [workingUntil, startedAt, userData, isStarting, selected]);

  const handleStartWork = async () => {
    try {
      setIsStarting(true);
      const token = await getAuth().currentUser.getIdToken();
      const res = await fetch("https://work-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ workSelected: selected }),
      });

      const data = await res.json();

      if (data?.success) {
        const now = Date.now();
        manualStartLock.current = now;
        
        setEarnings({ money: 0, xp: 0, gold: 0 });
        setStartedAt(now); 
        setWorkingUntil(Number(data.workUntil));
        setFrozen(false);
        addNotification(data.message, "success");
      }
      setTimeout(() => setIsStarting(false), 500);
    } catch (err) {
      setIsStarting(false);
      addNotification("Error starting job.", "error");
    }
  };

  const handleCollectEarnings = async (quitEarly = false) => {
    try {
      const token = await getAuth().currentUser.getIdToken();
      const res = await fetch("https://collectearnings-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ quitEarly }),
      });
      const data = await res.json();
      if (data?.success) {
        setWorkingUntil(null);
        setStartedAt(null);
        setEarnings({ money: 0, xp: 0, gold: 0 });
        addNotification(data.message || "Collected!", "success");
      }
    } catch (err) {
      addNotification("Error collecting.", "error");
    }
  };

useEffect(() => {
  if (!workingUntil || !startedAt || !userData || isStarting) return;

const interval = setInterval(() => {
  const now = Date.now();
  
  const effectiveRank = Math.min(userData.userRank ?? 1, 12);

  const maxWorkMinutes = 60 + (effectiveRank - 1) * 30;
  const maxDurationMs = maxWorkMinutes * 60 * 1000;

  const totalElapsedMs = Math.min(now - startedAt, maxDurationMs);
  
  const remainingMs = workingUntil - now;
  const blockMs = 10 * 60 * 1000; 
  const cliffMs = 30 * 60 * 1000;

  let blocksToDisplay = 0;
  if (totalElapsedMs >= cliffMs) {
    blocksToDisplay = Math.floor(totalElapsedMs / blockMs);
  }

  if (blocksToDisplay > 0) {
    const mult = 1.5 + (effectiveRank / 12); 
    const prog = (effectiveRank - 1) / 11; 
    
    let m = 0, x = 0, g = 0;
    switch (selected) {
      case "TechnicalAnalysis": 
        m = blocksToDisplay * (3 + 7 * prog) * mult; 
        break;
      case "SignalHunting": 
        x = blocksToDisplay * (14 + 16 * prog) * mult; 
        break;
      case "AcceleratedTrading":
        m = blocksToDisplay * (3 + 7 * prog) * mult * 0.5;
        x = blocksToDisplay * (14 + 16 * prog) * mult * 0.5;
        break;
      case "MarketProspecting": 
        g = blocksToDisplay * (0.5 / 6) * mult; 
        break;
      default:
        console.warn(`Unknown work task selected: ${selected}`);
        break;
    }
    setEarnings({ money: m, xp: x, gold: g });
  } else {
    setEarnings({ money: 0, xp: 0, gold: 0 });
  }

  setTimeLeft(fmtRemain(Math.max(0, remainingMs)));
}, 1000);

  return () => clearInterval(interval);
}, [workingUntil, startedAt, userData, isStarting, selected]);

  const isWorkFinished = workingUntil && workingUntil <= Date.now();

const getPotential = (type) => {
  if (!userData) return "Calculating...";
  const userRank = userData.userRank ?? 1;
  const mult = 1.5 + (userRank / 12);
  const prog = (userRank - 1) / 11;
  
  switch (type) {
    case "TechnicalAnalysis": 
      return `$${(6 * (3 + 7 * prog) * mult).toFixed(2)} / hr`;
    case "SignalHunting": 
      return `${(6 * (14 + 16 * prog) * mult).toFixed(0)} XP / hr`;
    case "AcceleratedTrading":
      return `$${(3 * (3 + 7 * prog) * mult).toFixed(1)} + ${(6 * (7 + 8 * prog) * mult).toFixed(0)}XP / hr`;
    case "MarketProspecting": 
      return `${(6 * (0.5 / 6) * mult).toFixed(4)} GOLD / hr`;
    default: return "";
  }
};

return (
  <div className="tabbed-panel" style={{ 
    background: 'transparent', 
    height: '100%', 
    display: 'flex', 
    flexDirection: 'column',
    justifyContent: 'center', 
    boxSizing: 'border-box',
    overflow: 'hidden',
    padding: '0 4px' 
  }}>
    {!workingUntil ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        
        <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', letterSpacing: '0.05rem' }}>
          SELECT JOB
        </div>
        
        {/* Jobs Grid */}
<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
  {workOptions.map((opt) => (
    <Tooltip key={opt.value} text={getPotential(opt.value)}>
      <div 
        className={`nav-module ${selected === opt.value ? "active" : ""}`} 
        onClick={() => setSelected(opt.value)} 
        style={{ 
          height: '26px',
          border: '1px solid rgba(255,255,255,0.05)', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2px',
          cursor: 'pointer',
          textAlign: 'center'
        }}
      >
        <span className="module-label" style={{ 
          fontSize: '0.8rem',
          lineHeight: '1.1',
          textTransform: 'uppercase',
          pointerEvents: 'none'
        }}>
          {opt.label}
        </span>
      </div>
    </Tooltip>
  ))}
</div>

        {/* Start Button */}
<button 
  className="jetbrains-btn" 
  onClick={handleStartWork} 
  disabled={isStarting} 
  style={{ 
    width: '100%', 
    height: '24px', 
    fontSize: '0.55rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '4px',
    // THEME STYLES
    background: 'rgba(80, 227, 194, 0.1)',
    color: '#50e3c2',
    border: '1px solid rgba(80, 227, 194, 0.3)', 
    fontFamily: '"JetBrains Mono", monospace',
    textTransform: 'uppercase',
    letterSpacing: '0.05rem',
    cursor: isStarting ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 0 10px rgba(80, 227, 194, 0.05)'
  }}
  onMouseEnter={(e) => {
    e.target.style.background = 'rgba(80, 227, 194, 0.2)';
    e.target.style.border = '1px solid #50e3c2';
  }}
  onMouseLeave={(e) => {
    e.target.style.background = 'rgba(80, 227, 194, 0.1)';
    e.target.style.border = '1px solid rgba(80, 227, 194, 0.3)';
  }}
>
   {isStarting ? "INITIALIZING..." : "START WORKING"}
</button>
      </div>
    ) : (
      /* Working State */
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
          WORK IN PROGRESS
        </div>

        <div className="terminal-card" style={{ 
          padding: '6px', 
          background: 'rgba(0,0,0,0.1)', 
          border: '1px solid rgba(255,255,255,0.05)', 
          fontFamily: 'JetBrains Mono',
          textAlign: 'center'
        }}>
          <Tooltip text="Current Active Job">
            <p style={{ margin: '0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', marginBottom: '4px' }}>
              {workOptions.find(o => o.value === selected)?.label}
            </p>
          </Tooltip>
          
          <p style={{ margin: '0', fontSize: '0.8rem', lineHeight: '1.2' }}>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.55rem' }}>ACCRUED:</span><br/>
            {selected === "TechnicalAnalysis" && <span style={{ color: '#50e3c2' }}>${earnings.money.toFixed(2)}</span>}
            {selected === "SignalHunting" && <span style={{ color: '#ffb86c' }}>{earnings.xp.toFixed(0)} EXP</span>}
            {selected === "AcceleratedTrading" && <span style={{ color: '#50e3c2' }}>${earnings.money.toFixed(1)} <span style={{ color: '#ffb86c' }}>+{earnings.xp.toFixed(0)}EXP</span></span>}
            {selected === "MarketProspecting" && <span style={{ color: '#ffb86c' }}>{earnings.gold.toFixed(4)} UNITS</span>}
          </p>
          <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)' }}>
            {isWorkFinished ? 'READY' : timeLeft}
          </p>
        </div>

<button 
  className={`jetbrains-btn ${isWorkFinished ? 'success' : 'danger'}`} 
  onClick={() => handleCollectEarnings(!isWorkFinished)} 
  style={{ 
    width: '100%', 
    height: '24px', 
    fontSize: '0.55rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 'auto',
    background: isWorkFinished 
      ? 'rgba(80, 227, 194, 0.1)'
      : 'rgba(255, 85, 85, 0.1)',
    color: isWorkFinished ? '#50e3c2' : '#ff5555',
    border: `1px solid ${isWorkFinished ? 'rgba(80, 227, 194, 0.3)' : 'rgba(255, 85, 85, 0.3)'}`,
    fontFamily: '"JetBrains Mono", monospace',
    textTransform: 'uppercase',
    letterSpacing: '0.05rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  }}
  onMouseEnter={(e) => {
    e.target.style.background = isWorkFinished 
      ? 'rgba(80, 227, 194, 0.2)' 
      : 'rgba(255, 85, 85, 0.2)';
    e.target.style.borderColor = isWorkFinished ? '#50e3c2' : '#ff5555';
  }}
  onMouseLeave={(e) => {
    e.target.style.background = isWorkFinished 
      ? 'rgba(80, 227, 194, 0.1)' 
      : 'rgba(255, 85, 85, 0.1)';
    e.target.style.borderColor = isWorkFinished 
      ? 'rgba(80, 227, 194, 0.3)' 
      : 'rgba(255, 85, 85, 0.3)';
  }}
>
  {isWorkFinished ? "FINISH" : "QUIT EARLY"}
</button>
      </div>
    )}
  </div>
);
}